import React, { createContext, useState, useEffect } from 'react';
import * as SecureStore from 'expo-secure-store';
import { Alert } from 'react-native';
import { loginUser, getUserProfile } from '../api/api';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [userToken, setUserToken] = useState(null);
  const [userData, setUserData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Helper function to interact with storage (SecureStore or localStorage)
  const getStorageItem = async (key) => {
    try {
      // Check if we're in a web environment
      if (typeof window !== 'undefined' && window.localStorage) {
        return localStorage.getItem(key);
      } else {
        return await SecureStore.getItemAsync(key);
      }
    } catch (error) {
      console.error(`Error getting ${key} from storage:`, error);
      return null;
    }
  };

  const setStorageItem = async (key, value) => {
    try {
      // Check if we're in a web environment
      if (typeof window !== 'undefined' && window.localStorage) {
        localStorage.setItem(key, value);
      } else {
        await SecureStore.setItemAsync(key, value);
      }
    } catch (error) {
      console.error(`Error setting ${key} in storage:`, error);
    }
  };

  const removeStorageItem = async (key) => {
    try {
      // Check if we're in a web environment
      if (typeof window !== 'undefined' && window.localStorage) {
        localStorage.removeItem(key);
      } else {
        await SecureStore.deleteItemAsync(key);
      }
    } catch (error) {
      console.error(`Error removing ${key} from storage:`, error);
    }
  };

  // Load token from storage
  useEffect(() => {
    const loadToken = async () => {
      try {
        const token = await getStorageItem('userToken');
        if (token) {
          setUserToken(token);
          await loadUserProfile();
        }
      } catch (error) {
        console.error('Error loading token', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadToken();
  }, []);

  // Load user profile
  const loadUserProfile = async () => {
    try {
      const profileData = await getUserProfile();
      setUserData(profileData);
      return profileData;
    } catch (error) {
      console.error('Error loading user profile', error);
      // If error occurs during profile fetch, log user out
      await logout();
      return null;
    }
  };

  // Login function
  const login = async (username, password) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const { token, user } = await loginUser(username, password);
      
      await setStorageItem('userToken', token);
      setUserToken(token);
      setUserData(user);
       await loadUserProfile();
      return true;
    } catch (error) {
      setError('Invalid username or password');
      console.error('Login error', error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // Logout function
  const logout = async () => {
    setIsLoading(true);
    try {
	//console.log('AuthContext: Starting logout process...');								 
      await removeStorageItem('userToken');
      await removeStorageItem('selectedClientId');
      setUserToken(null);
      setUserData(null);
	//console.log('AuthContext: Logout completed successfully');															
    } catch (error) {
      console.error('Logout error', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Context value
  const authContext = {
    login,
    logout,
    userToken,
    userData,
    isLoading,
    error,
    refreshUserData: loadUserProfile,
  };

  return (
    <AuthContext.Provider value={authContext}>
      {children}
    </AuthContext.Provider>
  );
};